import React, { useEffect, useState } from "react";
import {
  ChoiceGroup,
  Dropdown,
  FontWeights,
  IconButton,
  IDropdownOption,
  ResponsiveMode,
  Spinner,
  SpinnerSize,
  TeachingBubble,
  Toggle,
} from "@fluentui/react";
import { TextField } from "@fluentui/react/lib/TextField";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { FormatTypeEnum } from "../../api/models/format-type-enum";
import { FormatPreview } from "../../api/models/format-preview";
import { formatPreview } from "../../api/document/Format";
import {
  getDefaultBuiltInFormattingString,
  getDefaultCustomFormattingString,
} from "../../utils/helpers/ObjectFormattingHelper";
import { builtInDateFormat, builtInNumberFormat, builtInPercentageFormat } from "../../utils/constants/formats";
import { ImageDropdown } from "../coobjects/ImageDropdown";
import { getTemplates } from "../../api/document/Templates";
import { optionsLocale } from "../../utils/constants/locale";
import { DropdownSearch } from "../inputs/DropdownSearch";
import { isNotAuthorizedUser } from "../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { UserFunction } from "../../api/models/user-function";
import { useAppSelector } from "../../redux/hooks/hooks";
import {
  selectCpmMetadata,
  selectIxbrlSettings,
  selectServiceProvider,
  selectUserFunctions,
} from "../../redux/feature/settings/configurations-slice";
import { ServiceProvider } from "../../api/models/service-provider";
import { hasNoDefaultServiceProvider } from "../../pages/seahorse/SeahorseUtils";

import { DimensionParameter } from "../../api/models/dimension-parameter";
import { CoObjectAdvanced } from "../interfaces/CoObject";
import { getHTMLElementAsButtonProps } from "../../common/accessibility/HTMLElementAsButtonARIA";
import { ToggleContent } from "../inputs/ToggleContent";
import { ActionableTextField } from "../inputs/ActionableTextField";
import { CustomModal } from "../layout/ActionModal";
import { sanitizeFileName } from "../../utils/helpers/SanitizeHelper";
import {
  fetchInDesignLinkedDocument,
  INDESIGN_FOLDER_NAME,
  INDESIGN_LINK_NAME_PREFIX,
} from "../../api/document/InDesign";
import { DocumentPartSettings } from "../../api/models/document-part-settings";
import NotificationManager from "../../common/notification/NotificationManager";
import { INDESIGN_NOTIFICATION } from "../../utils/constants/notification";
import { DEFAULT_FOLDER_CREATION } from "./WithApiFileLocationProvider";
import { normalizePath } from "../../redux/feature/document/document-slice";
import { fetchDocumentPath } from "../../api/document/Documents";
import { isAskAiFeatureEnabled } from "../../api/config/ExternalConfig";
import { CAPTION_ADVANCED_TEMPLATE_DISABLED_TOOLTIP } from "./outlineCaptions";
import { CPMVersions, isCpmVersionLess } from "../../utils/helpers/ApiResolver";

const validate = (input: CoObjectAdvanced) => {
  const formatMetadata = input?.formatMetadata;
  const isValidValue = (val: string | number | undefined | null) => val !== undefined && val !== null && val !== "";
  return (
    formatMetadata !== undefined &&
    isValidValue(formatMetadata.numberFormatValue) &&
    isValidValue(formatMetadata.percentageFormatValue) &&
    isValidValue(formatMetadata.dateFormatValue)
  );
};

interface DocumentAdvancedProps {
  isRoot?: boolean;
  documentId?: string;
  documentName?: string;
  documentPath?: string;
  documentAdvanced: CoObjectAdvanced;
  updateDocumentAdvanced: (
    doc: CoObjectAdvanced,
    valid: boolean,
    mustAlignChildren?: boolean,
    mustUpdateDocumentToFab?: boolean
  ) => void;
  documentSettings: DocumentPartSettings;
  updateDocumentSettings: (doc: DocumentPartSettings, mustUpdateDocumentToFab?: boolean) => void;
  isEdit: boolean;
  readonly?: boolean;
  parameters: DimensionParameter[];
  goToParametersPage: () => void;
  isDuplicate: boolean;
}

interface PreviewState {
  previewNumber: string;
  previewPercentage: string;
  previewDate: string;
}

export interface NumberState {
  numberType: string;
  numberFormat: string;
}

export interface PercentageState {
  percentageType: string;
  percentageFormat: string;
}

export interface DateState {
  dateType: string;
  dateFormat: string;
}

export interface InfoState {
  teachingBubbleVisible: boolean;
}

const DEFAULT_VALUE = "1234567.89";
const DEFAULT_DATE = "01/01/2000";
const TYPE_DEFAULT = "Default";
const TYPE_CUSTOM = "Custom";
const DEFAULT_INDESIGN_LINK_NAME = INDESIGN_LINK_NAME_PREFIX + "Linked Document.docx";

export const inDesignFolderPath = (documentPath: string) => {
  return DEFAULT_FOLDER_CREATION + "/" + normalizePath(documentPath).replace(/^\//, "");
};

export const AdvancedDocumentPanel: React.FunctionComponent<DocumentAdvancedProps> = (props) => {
  const [state, setState] = useState<PreviewState>({
    previewNumber: "",
    previewPercentage: "",
    previewDate: "",
  });

  const {
    isRoot,
    documentId,
    documentName,
    documentPath,
    documentAdvanced,
    updateDocumentAdvanced,
    documentSettings,
    updateDocumentSettings,
    goToParametersPage,
    readonly,
  } = props;

  const [firstReadRagIndexState, setFirstReadRagIndexState] = useState<boolean | undefined>(undefined);
  const [sharepointFolder, setSharepointFolder] = useState<string | null | undefined>(undefined);
  const [inDesignEditableLinkName, setInDesignEditableLinkName] = useState<string>("");
  const [inDesignLinkNameEditorOpen, setInDesignLinkNameEditorOpen] = useState<boolean>(false);
  const extractEditableLinkName = (value: string) => {
    const escapedPrefix = INDESIGN_LINK_NAME_PREFIX.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const prefixRegex = new RegExp(`^${escapedPrefix}`);
    if (value.trim() === "") {
      value = documentName ?? "Untitled";
    }
    return value
      .trim()
      .replace(prefixRegex, "")
      .replace(/\.docx$/i, "")
      .trim();
  };

  useEffect(() => {
    if (firstReadRagIndexState === undefined) {
      setFirstReadRagIndexState(documentSettings?.isRagIndexingEnabled);
    }
  }, []);

  const updateInDesignEditableLinkName = (value: string) => {
    setInDesignEditableLinkName(extractEditableLinkName(value));
  };
  useEffect(() => {
    updateInDesignEditableLinkName(documentAdvanced.inDesignLink?.linkName ?? "");
  }, [documentAdvanced.inDesignLink]);

  useEffect(() => {
    if (!documentSettings?.isInDesignEnabled || sharepointFolder !== undefined) {
      return;
    }

    if (documentPath) {
      setSharepointFolder(inDesignFolderPath(documentPath + "/" + INDESIGN_FOLDER_NAME));
    } else if (documentId) {
      fetchDocumentPath(documentId, { params: { notShowLoading: true } })
        .then((path) => {
          setSharepointFolder(inDesignFolderPath(path + "/" + INDESIGN_FOLDER_NAME));
        })
        .catch(() => {
          setSharepointFolder(null);
          console.error("Could not obtain document path.");
        });
    } else {
      setSharepointFolder(null);
      console.error("No documentPath or documentId provided. Can not determine document path.");
    }
  }, [documentId, documentPath, documentSettings?.isInDesignEnabled, sharepointFolder]);

  useEffect(() => {
    if (!props.isEdit || !documentSettings?.isInDesignEnabled || !documentId) {
      return;
    }
    fetchInDesignLinkedDocument(documentId, { params: { notShowLoading: true } }).then((linkedDoc) => {
      if (!linkedDoc) {
        console.warn("No InDesign Linked Document info found.");
        return;
      }
      if (!linkedDoc.exists) {
        NotificationManager.addNotification({
          key: INDESIGN_NOTIFICATION,
          message: formatMessage("cof.documentmanagement.outline.edit.advanced.indesignlink.previous_link_not_found"),
          title: "InDesign",
          type: "info",
          closable: true,
          removeOnClose: true,
          isTemporary: true,
        });
      }
    });
  }, [documentId, props.isEdit, documentSettings?.isInDesignEnabled]);

  const [stateNumber, setStateNumber] = useState<NumberState>({
    numberType: documentAdvanced?.formatMetadata?.numberType ?? TYPE_CUSTOM,
    numberFormat: documentAdvanced?.formatMetadata?.numberFormatValue ?? "",
  });

  const [statePercentage, setStatePercentage] = useState<PercentageState>({
    percentageType: documentAdvanced?.formatMetadata?.percentageType ?? TYPE_CUSTOM,
    percentageFormat: documentAdvanced?.formatMetadata?.percentageFormatValue ?? "",
  });

  const [stateDate, setStateDate] = useState<DateState>({
    dateType: documentAdvanced?.formatMetadata?.dateType ?? TYPE_CUSTOM,
    dateFormat: documentAdvanced?.formatMetadata?.dateFormatValue ?? "",
  });

  const [currentTeachingBubble, setCurrentTeachingBubble] = useState<"image" | "format" | "template">(null);

  const [templates, setTemplates] = useState<IDropdownOption[]>([]);

  useEffect(() => {
    void getInitialFormatPreview();
    const fetchAndSetTemplates = async () => {
      try {
        const fetchedTemplates = await getTemplates();
        const templateOptions = fetchedTemplates.map((t) => ({
          key: t.templateId,
          text: t.templateName,
        }));

        templateOptions.push({
          key: "default",
          text: formatMessage("cof.newdocument.template.default"),
        });

        // If templateInfo exists, ensure it is in the list
        if (documentAdvanced.templateInfo) {
          const selectedTemplate = templateOptions.find((t) => t.key === documentAdvanced.templateInfo.templateId);
          if (!selectedTemplate) {
            templateOptions.unshift({
              key: documentAdvanced.templateInfo.templateId,
              text: documentAdvanced.templateInfo.templateName,
            });
          }
        }

        setTemplates(templateOptions);
      } catch (error) {
        console.error("Error fetching templates:", error);
        setTemplates([{ key: "default", text: formatMessage("cof.newdocument.template.default") }]);
      }
    };

    if (!props.isEdit || props.isDuplicate || !templates.length) {
      void fetchAndSetTemplates();
    }
  }, [props.isEdit, props.isDuplicate]); // Keep the dependency array minimal

  const getInitialFormatPreview = async (refreshProps?: {
    locale: string;
    stateNumber: NumberState;
    statePercentage: PercentageState;
    stateDate: DateState;
  }) => {
    let locale: string;
    let numberFormatProps: NumberState;
    let percentageFormatProps: PercentageState;
    let dateFormatProps: DateState;

    if (refreshProps) {
      locale = refreshProps.locale;
      numberFormatProps = refreshProps.stateNumber;
      percentageFormatProps = refreshProps.statePercentage;
      dateFormatProps = refreshProps.stateDate;
    } else {
      locale = documentAdvanced?.formatMetadata?.locale;
      numberFormatProps = {
        numberFormat:
          documentAdvanced?.formatMetadata?.numberFormatValue ??
          getDefaultCustomFormattingString(FormatTypeEnum.NUMBER),
        numberType: documentAdvanced?.formatMetadata?.numberType ?? TYPE_CUSTOM,
      };
      percentageFormatProps = {
        percentageFormat:
          documentAdvanced?.formatMetadata?.percentageFormatValue ??
          getDefaultCustomFormattingString(FormatTypeEnum.PERCENTAGE),
        percentageType: documentAdvanced?.formatMetadata?.percentageType ?? TYPE_CUSTOM,
      };
      dateFormatProps = {
        dateFormat:
          documentAdvanced?.formatMetadata?.dateFormatValue ?? getDefaultCustomFormattingString(FormatTypeEnum.DATE),
        dateType: documentAdvanced?.formatMetadata?.dateType ?? TYPE_CUSTOM,
      };
    }

    const number: FormatPreview = {
      value: DEFAULT_VALUE,
      format: numberFormatProps.numberFormat,
      typeFormatChoice: numberFormatProps.numberType,
      typeFormat: FormatTypeEnum.NUMBER,
      locale,
    };
    const percentage: FormatPreview = {
      value: DEFAULT_VALUE,
      format: percentageFormatProps.percentageFormat,
      typeFormatChoice: percentageFormatProps.percentageType,
      typeFormat: FormatTypeEnum.PERCENTAGE,
      locale,
    };
    const date: FormatPreview = {
      value: DEFAULT_DATE,
      format: dateFormatProps.dateFormat,
      typeFormatChoice: dateFormatProps.dateType,
      typeFormat: FormatTypeEnum.DATE,
      locale,
    };
    const initialState: PreviewState = {
      previewNumber: "",
      previewPercentage: "",
      previewDate: "",
    };
    const promises: Promise<void>[] = [
      formatPreview(number).then((v) => {
        initialState.previewNumber = v;
      }),
      formatPreview(percentage).then((v) => {
        initialState.previewPercentage = v;
      }),
      formatPreview(date).then((v) => {
        initialState.previewDate = v;
      }),
    ];
    await Promise.all(promises);
    setState(initialState);
  };

  const optionsChoice = [
    { key: TYPE_DEFAULT, text: formatMessage("cof.base.default") },
    { key: TYPE_CUSTOM, text: formatMessage("cof.base.custom") },
  ];

  const onChangeNumber = (value: string, numberType: string) => {
    if (readonly) return;
    const newDocAdvanced: CoObjectAdvanced = {
      ...documentAdvanced,
      formatMetadata: {
        ...documentAdvanced.formatMetadata,
        numberFormatValue: value,
        numberType: numberType,
      },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    setStateNumber((s) => ({ ...s, numberFormat: value, numberType: numberType }));
    if (value?.length) {
      formatPreview({
        value: DEFAULT_VALUE,
        format: value,
        typeFormatChoice: numberType,
        locale: documentAdvanced?.formatMetadata?.locale,
        typeFormat: FormatTypeEnum.NUMBER,
      }).then((previewN) => setState((s) => ({ ...s, previewNumber: previewN })));
    } else {
      setState((s) => ({ ...s, previewNumber: "" }));
    }
  };

  const onChangeTemplate = (choice: IDropdownOption) => {
    if (readonly) return;
    const newDocAdvanced: CoObjectAdvanced = {
      ...documentAdvanced,
      templateInfo: { templateId: choice.key.toString(), templateName: choice.text },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
  };

  const onChangeLocale = (choice: IDropdownOption) => {
    if (readonly) return;
    const newLocale = choice.key.toString();

    const newDocAdvanced: CoObjectAdvanced = {
      ...documentAdvanced,
      formatMetadata: {
        ...documentAdvanced.formatMetadata,
        locale: newLocale,
      },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    void getInitialFormatPreview({ locale: newLocale, stateNumber, statePercentage, stateDate });
  };

  const onChangePercentage = (value: string, percentageType: string) => {
    if (readonly) return;
    const newDocAdvanced: CoObjectAdvanced = {
      ...documentAdvanced,
      formatMetadata: {
        ...documentAdvanced.formatMetadata,
        percentageFormatValue: value,
        percentageType: percentageType,
      },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    setStatePercentage((s) => ({ ...s, percentageFormat: value, percentageType: percentageType }));
    if (value?.length) {
      formatPreview({
        value: DEFAULT_VALUE,
        format: value,
        typeFormatChoice: percentageType,
        locale: documentAdvanced?.formatMetadata?.locale,
        typeFormat: FormatTypeEnum.PERCENTAGE,
      }).then((previewN) => setState((s) => ({ ...s, previewPercentage: previewN })));
    } else {
      setState((s) => ({ ...s, previewPercentage: "" }));
    }
  };

  const onChangeDate = (value: string, dateType: string) => {
    if (readonly) return;
    const newDocAdvanced: CoObjectAdvanced = {
      ...documentAdvanced,
      formatMetadata: {
        ...documentAdvanced.formatMetadata,
        dateFormatValue: value,
        dateType: dateType,
      },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    setStateDate((s) => ({ ...s, dateFormat: value, dateType: dateType }));
    if (value?.length) {
      formatPreview({
        value: DEFAULT_DATE,
        format: value,
        typeFormatChoice: dateType,
        locale: documentAdvanced?.formatMetadata?.locale,
        typeFormat: FormatTypeEnum.DATE,
      }).then((previewD) => setState((s) => ({ ...s, previewDate: previewD })));
    } else {
      setState((s) => ({ ...s, previewDate: "" }));
    }
  };

  const handleChangeImageExtension = (selectedKey: string) => {
    if (readonly) return;
    const newDocAdvanced: CoObjectAdvanced = {
      ...documentAdvanced,
      formatImage: {
        ...documentAdvanced.formatImage,
        imageExtension: selectedKey,
      },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
  };

  const handleChangeImageResolution = (selectedKey: number, modified: boolean) => {
    if (readonly) return;
    if (modified) {
      const newDocAdvanced: CoObjectAdvanced = {
        ...documentAdvanced,
        formatImage: {
          ...documentAdvanced.formatImage,
          imageResolution: selectedKey,
        },
      };
      updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced));
    }
  };

  const handleChangeInDesignSettings = (enabled: boolean, linkName: string) => {
    if (readonly) return;
    setInDesignLinkNameEditorOpen(false);

    if (linkName.trim() === "") {
      if (documentName && documentName.trim() !== "") {
        linkName = documentName;
      } else {
        linkName = DEFAULT_INDESIGN_LINK_NAME;
      }
      console.warn("Using default InDesign link name:", linkName);
    }

    const newDocAdvanced: CoObjectAdvanced = {
      ...documentAdvanced,
      inDesignLink: {
        linkName: INDESIGN_LINK_NAME_PREFIX + extractEditableLinkName(sanitizeFileName(linkName)) + ".docx",
      },
    };
    updateDocumentAdvanced(newDocAdvanced, validate(newDocAdvanced), false);
    const newDocSettings: DocumentPartSettings = {
      ...documentSettings,
      isInDesignEnabled: enabled,
    };
    updateDocumentSettings(newDocSettings);
  };

  const handleChangeRagIndexingSettings = (enabled: boolean) => {
    if (readonly) return;
    const newDocSettings: DocumentPartSettings = {
      ...documentSettings,
      isRagIndexingEnabled: enabled,
    };
    updateDocumentSettings(newDocSettings, firstReadRagIndexState != enabled);
  };

  const customToggleStyles = {
    label: {
      fontWeight: FontWeights.regular,
      display: "inline-block",
      marginRight: "5px",
    },
    container: {
      display: "inline-block",
    },
  };

  const handleIXBRLToggle = (isChecked: boolean) => {
    if (readonly) return;
    setIsIxbrlSettingsAvailable(isChecked);
    const newDocSettings: DocumentPartSettings = {
      ...documentSettings,
      isIxbrlEnabled: isChecked,
    };
    updateDocumentSettings(newDocSettings);
  };

  const openInDesignLinkEditor = () => {
    if (readonly) return;
    setInDesignLinkNameEditorOpen(true);
  };

  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const notAuthorizedUserForIXBRL = isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_ADMIN, userFunctions);
  const cpmMetadata = useAppSelector(selectCpmMetadata);
  // Ask AI is shown only when: the client feature flag is on (featureFlags.askAi), the tenant-level
  // system property is enabled (IDS-5564: it.grupposervizi.easy.askai.enabled), and the user owns the
  // "Consistency Controller Agent" admin function (IDS-5565: cod 0004325).
  const showAskAiToggle =
    isAskAiFeatureEnabled() &&
    !!cpmMetadata?.askAiEnabled &&
    (!isNotAuthorizedUser(UserFunctionEnum.CONSISTENCY_CONTROLLER_AGENT, userFunctions) ||
      isCpmVersionLess(CPMVersions.CPM_VERSION38_0));
  const defaultServiceProvider: ServiceProvider = useAppSelector(selectServiceProvider);
  const allIxbrlSettings = useAppSelector(selectIxbrlSettings);
  let [isIxbrlSettingsAvailable, setIsIxbrlSettingsAvailable] = useState(false);

  useEffect(() => {
    if (documentSettings?.isIxbrlEnabled !== undefined) {
      setIsIxbrlSettingsAvailable(documentSettings.isIxbrlEnabled);
      return;
    }
    if (allIxbrlSettings && Array.isArray(allIxbrlSettings)) {
      allIxbrlSettings.forEach((setting) => {
        if (documentId === setting.documentId) {
          setIsIxbrlSettingsAvailable(setting.isChecked);
        }
      });
    }
  }, [documentId, allIxbrlSettings, documentSettings?.isIxbrlEnabled]);

  const infoId = "AdvancedDocumentPanelInfoId";
  const imageFormatInfoId = "AdvancedDocumentPanelImageFormatInfoId";
  const templateInfoId = "AdvancedDocumentPanelTemplateInfoId";
  let dimensions = props.parameters?.filter((parameterObject) => parameterObject.dimensions?.length > 0);
  return (
    <>
      {!props.isEdit && dimensions.length == 0 ? (
        <div
          style={{
            height: "30px",
            lineHeight: "15px",
            backgroundColor: "darkorange",
            fontSize: "0.8rem",
            color: "white",
            marginTop: "0.7rem",
            marginBottom: "0.7rem",
            padding: "0.5rem 0.5rem 0.5rem 0.5rem",
            borderRadius: "5px",
          }}
        >
          <img
            alt="warning"
            src={"/assets/warning-white.svg"}
            style={{
              justifyContent: "left",
              width: "15px",
              height: "15px",
              objectFit: "contain",
            }}
          />
          <span
            style={{
              marginLeft: "0.5rem",
            }}
          >
            {formatMessage("cof.dimensions.notconfigured")}
          </span>
          <span
            style={{
              float: "right",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            {...getHTMLElementAsButtonProps({
              onClick: goToParametersPage,
            })}
          >
            {formatMessage("cof.base.configure")}
          </span>
        </div>
      ) : null}
      {!notAuthorizedUserForIXBRL && (
        <>
          <div className="ixbrlToggle">{"iXBRL"}</div>
          <div style={{ display: "inline-flex", float: "right" }}>
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12" style={{ marginTop: "5px" }}>
              <Toggle
                styles={customToggleStyles}
                checked={isIxbrlSettingsAvailable}
                disabled={!!readonly || hasNoDefaultServiceProvider(defaultServiceProvider)}
                onChange={(_event, isChecked?: boolean) => handleIXBRLToggle(!!isChecked)}
              />
            </div>
          </div>
        </>
      )}
      {isRoot && showAskAiToggle && (
        <ToggleContent
          label={formatMessage("cof.documentmanagement.outline.edit.advanced.ragindexing.enable")}
          checked={documentSettings?.isRagIndexingEnabled ?? false}
          disabled={!!readonly}
          onChange={(_e, isChecked) => handleChangeRagIndexingSettings(isChecked)}
        />
      )}
      {isRoot && (
        <ToggleContent
          label="InDesign"
          checked={documentSettings?.isInDesignEnabled ?? false}
          disabled={!!readonly}
          onChange={(_e, isChecked) =>
            handleChangeInDesignSettings(isChecked, documentAdvanced.inDesignLink?.linkName ?? "")
          }
        >
          <ActionableTextField
            readOnly
            label={formatMessage("cof.documentmanagement.outline.edit.advanced.indesignlink.label")}
            value={documentAdvanced.inDesignLink?.linkName ?? DEFAULT_INDESIGN_LINK_NAME}
            description={sharepointFolder ?? undefined}
            disabled={!!readonly}
            onDoubleClick={openInDesignLinkEditor}
            actions={[
              {
                key: "edit",
                label: "Edit",
                iconProps: { iconName: "EditSolid12" },
                onClick: openInDesignLinkEditor,
              },
            ]}
          />
          {sharepointFolder === undefined && (
            <div style={{ padding: "4px 0", display: "flex", justifyContent: "flex-start" }}>
              <Spinner size={SpinnerSize.xSmall} id="spinner" />
            </div>
          )}
        </ToggleContent>
      )}
      {
        // TODO as described into IDS-1387
        <>
          {currentTeachingBubble === "template" && (
            <TeachingBubble
              calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
              target={`#${templateInfoId}`}
              hasCloseButton={true}
              closeButtonAriaLabel="Close"
              onDismiss={() => setCurrentTeachingBubble(null)}
              headline={formatMessage("cof.createnewdocument.template.info.title")}
            >
              <text>
                {formatMessage(
                  props.isEdit
                    ? CAPTION_ADVANCED_TEMPLATE_DISABLED_TOOLTIP
                    : "cof.createnewdocument.template.info.message"
                )}
              </text>
            </TeachingBubble>
          )}
          <div
            style={{
              fontSize: "1rem",
              fontWeight: "500",
              lineHeight: "1.25rem",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              color: "var(--color-blue)",
            }}
          >
            {formatMessage("cof.newdocument.templateoptions")}
            <IconButton
              id={templateInfoId}
              aria-label="Info"
              iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
              onClick={() => setCurrentTeachingBubble("template")}
              data-testid={templateInfoId}
            />
          </div>
          <div className="ms-Grid" dir="ltr">
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
                <Dropdown
                  data-testid={"templates-input"}
                  options={templates}
                  placeholder={formatMessage("cof.newdocument.template.default")}
                  onChange={(_event, option) => {
                    onChangeTemplate(option);
                  }}
                  selectedKey={documentAdvanced.templateInfo?.templateId}
                  responsiveMode={ResponsiveMode.large}
                  disabled={!!readonly || props.isEdit}
                  label={formatMessage("cof.base.template")}
                />
              </div>
            </div>
          </div>
        </>
      }
      {currentTeachingBubble === "format" && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${infoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setCurrentTeachingBubble(null)}
          headline={formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.title")}
        >
          <text>{formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.message")}</text>
        </TeachingBubble>
      )}
      <div
        style={{
          fontSize: "1rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.base.formatoptions")}
        <IconButton
          id={infoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          onClick={() => setCurrentTeachingBubble("format")}
        />
      </div>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <DropdownSearch
              data-testid={"locale-input"}
              options={optionsLocale}
              placeholder={formatMessage("cof.base.select")}
              disabled={!!readonly}
              onChange={(_event, option) => {
                onChangeLocale(option);
              }}
              selectedKey={documentAdvanced?.formatMetadata?.locale}
              responsiveMode={ResponsiveMode.large}
              label={formatMessage("cof.base.locale")}
            />
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              selectedKey={stateNumber?.numberType}
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              disabled={!!readonly}
              onChange={(_, choice) => {
                onChangeNumber(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(FormatTypeEnum.NUMBER)
                    : getDefaultBuiltInFormattingString(FormatTypeEnum.NUMBER),
                  choice.key
                );
              }}
              label={formatMessage("cof.base.number")}
            />
            {stateNumber.numberType === TYPE_DEFAULT && (
              <Dropdown
                data-testid={"number-default"}
                options={builtInNumberFormat}
                disabled={!!readonly}
                onChange={(_event, option) => {
                  onChangeNumber(option.key.toString(), stateNumber.numberType);
                }}
                selectedKey={
                  stateNumber.numberFormat ? builtInNumberFormat.find((f) => f.key == stateNumber.numberFormat)?.key : 1
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {stateNumber.numberType === TYPE_CUSTOM && (
              <TextField
                value={stateNumber.numberFormat ?? ""}
                disabled={!!readonly}
                onChange={(e) => onChangeNumber((e.target as HTMLInputElement)?.value ?? "", stateNumber.numberType)}
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.previewNumber}</div>
            </div>
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              selectedKey={statePercentage.percentageType}
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              disabled={!!readonly}
              onChange={(_, choice) => {
                onChangePercentage(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(FormatTypeEnum.PERCENTAGE)
                    : getDefaultBuiltInFormattingString(FormatTypeEnum.PERCENTAGE),
                  choice.key
                );
              }}
              label={formatMessage("cof.base.percentage")}
            />
            {statePercentage.percentageType === TYPE_DEFAULT && (
              <Dropdown
                data-testid={"percentage-default"}
                options={builtInPercentageFormat}
                disabled={!!readonly}
                onChange={(_event, option) => {
                  onChangePercentage(option.key.toString(), statePercentage.percentageType);
                }}
                selectedKey={
                  statePercentage.percentageFormat
                    ? builtInPercentageFormat.find((f) => f.key == statePercentage.percentageFormat)?.key
                    : 1
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {statePercentage.percentageType == TYPE_CUSTOM && (
              <TextField
                value={statePercentage.percentageFormat ?? ""}
                disabled={!!readonly}
                onChange={(e) =>
                  onChangePercentage((e.target as HTMLInputElement)?.value ?? "", statePercentage.percentageType)
                }
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.previewPercentage}</div>
            </div>
          </div>
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              selectedKey={stateDate.dateType}
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              disabled={!!readonly}
              onChange={(_, choice) => {
                onChangeDate(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(FormatTypeEnum.DATE)
                    : getDefaultBuiltInFormattingString(FormatTypeEnum.DATE),
                  choice.key
                );
              }}
              label={formatMessage("cof.base.date")}
            />
            {stateDate.dateType == TYPE_DEFAULT && (
              <Dropdown
                data-testid={"date-default"}
                options={builtInDateFormat}
                disabled={!!readonly}
                onChange={(_event, option) => {
                  onChangeDate(option.key.toString(), stateDate.dateType);
                }}
                selectedKey={
                  stateDate.dateFormat ? builtInDateFormat.find((f) => f.key == stateDate.dateFormat)?.key : 1
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {stateDate.dateType == TYPE_CUSTOM && (
              <TextField
                value={stateDate.dateFormat ?? ""}
                disabled={!!readonly}
                onChange={(e) => onChangeDate((e.target as HTMLInputElement)?.value ?? "", stateDate.dateType)}
              />
            )}
            <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.previewDate}</div>
          </div>
        </div>
      </div>

      {currentTeachingBubble === "image" && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${imageFormatInfoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setCurrentTeachingBubble(null)}
          headline={formatMessage("cof.createnewdocument.imagesformatting.info.title")}
        >
          <text>{formatMessage("cof.createnewdocument.imagesformatting.info.message")}</text>
        </TeachingBubble>
      )}
      <div
        style={{
          fontSize: "1rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.createnewdocument.imagesformatting")}
        <IconButton
          id={imageFormatInfoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          onClick={() => setCurrentTeachingBubble("image")}
        />
      </div>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <ImageDropdown
            imageExtension={documentAdvanced?.formatImage?.imageExtension}
            imageResolution={documentAdvanced?.formatImage?.imageResolution}
            disabled={!!readonly}
            required={false}
            onExtensionChange={handleChangeImageExtension}
            onResolutionChange={handleChangeImageResolution}
          />
        </div>
      </div>
      {!readonly && inDesignLinkNameEditorOpen && (
        <CustomModal
          modalTitle={formatMessage("cof.documentmanagement.outline.edit.advanced.indesignlink.rename.title")}
          messageTitle={formatMessage("cof.documentmanagement.outline.edit.advanced.indesignlink.rename.message.title")}
          message={formatMessage("cof.documentmanagement.outline.edit.advanced.indesignlink.rename.message")}
          type="confirm"
          onHide={() => {}}
          actions={[
            {
              id: "back_button",
              title: formatMessage("cof.base.back"),
              onClick: () => {
                setInDesignLinkNameEditorOpen(false);
                updateInDesignEditableLinkName(documentAdvanced.inDesignLink?.linkName ?? "");
              },
              variant: "white",
            },
            {
              id: "rename_button",
              title: formatMessage("cof.base.rename"),
              disabled: !!readonly || inDesignEditableLinkName.trim().length === 0,
              onClick: () => {
                handleChangeInDesignSettings(documentSettings?.isInDesignEnabled ?? true, inDesignEditableLinkName);
              },
              variant: "blue",
            },
          ]}
        >
          <TextField
            label={formatMessage("cof.documentmanagement.outline.edit.advanced.indesignlink.label")}
            prefix={INDESIGN_LINK_NAME_PREFIX}
            value={inDesignEditableLinkName}
            disabled={!!readonly}
            onChange={(_e, newValue) => setInDesignEditableLinkName(newValue)}
            suffix=".docx"
            description={sharepointFolder ?? undefined}
          />
        </CustomModal>
      )}
    </>
  );
};
